// src/hooks/useStreamingChat.js

import { useState } from 'react';

// Helper function to get API base URL for embedded widgets
const getApiBaseUrl = () => {
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src && scripts[i].src.includes('smushchat-widget.js')) {
      if (scripts[i].src.includes('localhost') || scripts[i].src.includes('127.0.0.1')) {
        return 'http://localhost:3000';
      } else if (scripts[i].src.includes('staging.smushlabs.ai')) {
        return 'https://staging.smushlabs.ai';
      } else if (scripts[i].src.includes('smushlabs.ai')) {
        return 'https://smushlabs.ai';
      } else if (scripts[i].src.includes('workers.dev')) {
        const url = new URL(scripts[i].src);
        return url.origin;
      }
    }
  }
  return window.location.origin; // Fallback for platform mode
};

export const useStreamingChat = () => {
  const [isCurrentlyStreaming, setIsCurrentlyStreaming] = useState(false);

  // Function to handle streaming response from server
  const handleStreamingResponse = async (chatbotId, message, threadId, retryAttempt = 0, setMessages, sanitizeResponse, chatAreaRef) => {
    const controller = new AbortController();
    const streamTimeout = 45000; // 45 seconds for all streaming requests

    const timeoutId = setTimeout(() => controller.abort(), streamTimeout);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatbotId,
          message,
          threadId,
          retryAttempt,
          stream: true // Enable streaming
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let finalThreadId = threadId;
      let isComplete = false;

      while (!isComplete) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === 'connection') {
                continue;
              }

              if (parsed.type === 'status') {
                continue;
              }

              if (parsed.type === 'text_delta') {
                // Update the accumulated text and display it
                accumulatedText = parsed.accumulated;

                // Capture the current value to avoid loop function warning
                const currentAccumulatedText = accumulatedText;

                // Force a state update to trigger re-render
                setMessages(prevMessages => {
                  const newMessages = [...prevMessages];
                  const lastMessageIndex = newMessages.length - 1;

                  // If last message is from assistant, update it
                  if (newMessages[lastMessageIndex] && newMessages[lastMessageIndex].role === 'assistant') {
                    // Create a completely new message object to ensure React re-render
                    newMessages[lastMessageIndex] = {
                      role: 'assistant',
                      content: sanitizeResponse(currentAccumulatedText),
                      isStreaming: true,
                      key: `streaming-${Date.now()}` // Force re-render with unique key
                    };
                  } else {
                    // Add new assistant message
                    newMessages.push({
                      role: 'assistant',
                      content: sanitizeResponse(currentAccumulatedText),
                      isStreaming: true,
                      key: `streaming-${Date.now()}`
                    });
                  }

                  return newMessages;
                });

                // Force scroll to bottom to show new text
                setTimeout(() => {
                  if (chatAreaRef.current) {
                    chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
                  }
                }, 10);

                continue;
              }

              if (parsed.type === 'complete') {
                accumulatedText = parsed.message;
                if (parsed.threadId) {
                  finalThreadId = parsed.threadId;
                }

                // Capture the final value to avoid loop function warning
                const finalAccumulatedText = accumulatedText;

                // Final update to remove streaming indicator and show complete message
                setMessages(prevMessages => {
                  const newMessages = [...prevMessages];
                  const lastMessageIndex = newMessages.length - 1;

                  if (newMessages[lastMessageIndex] && newMessages[lastMessageIndex].role === 'assistant') {
                    newMessages[lastMessageIndex] = {
                      role: 'assistant',
                      content: sanitizeResponse(finalAccumulatedText),
                      isStreaming: false // This allows form logic to count the message
                    };
                  }

                  return newMessages;
                });

                // Reset streaming state to allow forms to show
                setIsCurrentlyStreaming(false);

                isComplete = true;
                continue;
              }

              // HUMAN-IN-THE-LOOP: Check if human agent is handling this thread
              if (parsed.type === 'humanHandling' || parsed.humanHandling) {
                // Remove the temporary streaming message
                setMessages(prevMessages => {
                  const newMessages = [...prevMessages];
                  if (newMessages[newMessages.length - 1]?.role === 'assistant' && newMessages[newMessages.length - 1]?.isStreaming) {
                    newMessages.pop();
                  }
                  return newMessages;
                });

                setIsCurrentlyStreaming(false);

                // Return with humanHandling flag
                return {
                  message: null,
                  threadId: parsed.threadId || finalThreadId,
                  humanHandling: true
                };
              }

              if (parsed.type === 'error') {
                throw new Error(parsed.message);
              }

            } catch (parseError) {
              continue;
            }
          }
        }
      }

      // IMPORTANT: Handle case where stream ended without explicit completion
      if (!isComplete && accumulatedText) {

        // Capture the final accumulated text
        const finalAccumulatedText = accumulatedText;

        // Final cleanup - remove streaming indicator
        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessageIndex = newMessages.length - 1;

          if (newMessages[lastMessageIndex] && newMessages[lastMessageIndex].role === 'assistant') {
            newMessages[lastMessageIndex] = {
              role: 'assistant',
              content: sanitizeResponse(finalAccumulatedText),
              isStreaming: false // Stop the blinking cursor
            };
          }

          return newMessages;
        });

        // Force reset streaming state
        setIsCurrentlyStreaming(false);
      }

      // Return the final result
      return {
        message: accumulatedText,
        threadId: finalThreadId
      };

    } catch (error) {
      clearTimeout(timeoutId);

      // Always ensure streaming state is cleaned up on any error
      setIsCurrentlyStreaming(false);

      // Clean up any partial message with streaming indicator
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const lastMessageIndex = newMessages.length - 1;

        if (newMessages[lastMessageIndex] && newMessages[lastMessageIndex].role === 'assistant' && newMessages[lastMessageIndex].isStreaming) {
          // If there was partial content, keep it but remove streaming indicator
          if (newMessages[lastMessageIndex].content && newMessages[lastMessageIndex].content.trim()) {
            newMessages[lastMessageIndex] = {
              ...newMessages[lastMessageIndex],
              isStreaming: false
            };
          } else {
            // If no content, remove the empty streaming message
            newMessages.pop();
          }
        }

        return newMessages;
      });

      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try breaking your message into smaller parts.');
      }

      throw error;
    }
  };

  // Function to determine if streaming should be used
  const shouldUseStreaming = (message, hasCustomAssistant) => {
    // Enable streaming for all messages to provide better user experience
    return true;
  };

  return {
    isCurrentlyStreaming,
    setIsCurrentlyStreaming,
    handleStreamingResponse,
    shouldUseStreaming
  };
}; 